import { useEffect, useState, useMemo } from "react";
import {
  Truck, Save, Plus, Trash2, Settings as SettingsIcon, MapPin, Zap, Home, Store, Package,
  AlertCircle, RotateCcw, ChevronRight, X, GripVertical,
} from "lucide-react";
import api, { eur, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const ICONS = { home: Home, "map-pin": MapPin, zap: Zap, store: Store, package: Package };
function IconFor({ name }) {
  const C = ICONS[name] || Package;
  return <C className="h-5 w-5" strokeWidth={1.5} />;
}

const Lbl = ({ children }) => <label className="text-[11px] uppercase tracking-[0.15em] text-ash">{children}</label>;

const DEFAULT_METHOD = () => ({
  id: `method_${Date.now()}`,
  label: "Nouvelle méthode",
  carrier: "other",
  description: "",
  delivery_time: "",
  icon: "package",
  enabled: true,
  basis: "amount",
  zones: {},
});

const CARRIERS = [
  { value: "colissimo", label: "Colissimo" },
  { value: "chronopost", label: "Chronopost" },
  { value: "mondial_relay", label: "Mondial Relay" },
  { value: "dpd", label: "DPD" },
  { value: "ups", label: "UPS" },
  { value: "gls", label: "GLS" },
  { value: "pickup", label: "Retrait magasin" },
  { value: "other", label: "Autre" },
];

const ICON_CHOICES = [
  { value: "home", label: "Maison" },
  { value: "map-pin", label: "Point relais" },
  { value: "zap", label: "Express" },
  { value: "store", label: "Magasin" },
  { value: "package", label: "Colis" },
];

/* ------------------------------------------------------------------ */
/* Simulator                                                           */
/* ------------------------------------------------------------------ */
function Simulator({ config }) {
  const [country, setCountry] = useState((config?.zones?.[0]?.countries?.[0]) || "FR");
  const [subtotal, setSubtotal] = useState(50);
  const [weight, setWeight] = useState(0);
  const [opts, setOpts] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shipping/options", {
        params: { country, subtotal, weight },
      });
      setOpts(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-sand border border-stone rounded-sm p-5 mt-4">
      <h3 className="text-[11px] uppercase tracking-[0.15em] text-ash mb-3">Simulateur</h3>
      <div className="grid md:grid-cols-4 gap-3 items-end">
        <div>
          <Lbl>Pays</Lbl>
          <select value={country} onChange={(e) => setCountry(e.target.value)} className="mt-1 w-full border border-stone rounded-sm p-2.5 text-sm bg-white">
            {(config?.zones || []).flatMap((z) =>
              (z.countries || []).map((c) => <option key={`${z.code}-${c}`} value={c}>{c} ({z.label})</option>)
            )}
          </select>
        </div>
        <div>
          <Lbl>Total panier (€)</Lbl>
          <Input type="number" step="0.01" value={subtotal} onChange={(e) => setSubtotal(parseFloat(e.target.value) || 0)} className="mt-1" />
        </div>
        <div>
          <Lbl>Poids (kg, optionnel)</Lbl>
          <Input type="number" step="0.1" value={weight} onChange={(e) => setWeight(parseFloat(e.target.value) || 0)} className="mt-1" />
        </div>
        <button onClick={run} disabled={loading} data-testid="simulate-btn" className="bg-ink text-cream px-4 py-2.5 text-xs uppercase tracking-[0.15em] disabled:opacity-50">
          {loading ? "..." : "Simuler"}
        </button>
      </div>
      {opts && (
        <div className="mt-4 bg-white border border-stone rounded-sm overflow-hidden">
          {opts.options.length === 0 ? (
            <p className="p-4 text-sm text-graphite italic">Aucune méthode disponible pour ce pays / panier.</p>
          ) : (
            <ul>
              {opts.options.map((o) => (
                <li key={o.id} className="px-4 py-2.5 border-b border-stone last:border-0 flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-ink">
                    <IconFor name={o.icon} /> {o.label} <span className="text-ash">· {o.delivery_time}</span>
                  </span>
                  <span className="font-mono text-ink">{o.free ? "Offerte" : eur(o.price)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Method editor                                                       */
/* ------------------------------------------------------------------ */
function ZoneRulesEditor({ zone, rules, freeAbove, enabled, onChange }) {
  const update = (patch) => onChange({ rules, freeAbove, enabled, ...patch });

  const setRule = (idx, key, value) => {
    const newRules = rules.map((r, i) => i === idx ? { ...r, [key]: value } : r);
    update({ rules: newRules });
  };

  const addRule = () => {
    update({ rules: [...rules, { up_to: rules.length ? null : 30, price: 5 }] });
  };
  const removeRule = (idx) => {
    update({ rules: rules.filter((_, i) => i !== idx) });
  };

  return (
    <div className="border border-stone rounded-sm p-4 bg-white space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-ink">{zone.label} <span className="text-ash font-normal text-xs">({zone.code})</span></h4>
        <label className="flex items-center gap-2 text-sm text-graphite">
          <input type="checkbox" checked={enabled} onChange={(e) => update({ enabled: e.target.checked })} className="accent-copper" />
          Activé
        </label>
      </div>

      {enabled && (
        <>
          <div className="text-xs text-ash">Tranches (la première règle qui correspond s&apos;applique). <em>up_to vide = au-delà</em>.</div>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-ash border-b border-stone">
              <tr><th className="text-left py-1.5">Jusqu&apos;à</th><th className="text-left">Prix (€)</th><th></th></tr>
            </thead>
            <tbody>
              {rules.map((r, idx) => (
                <tr key={idx} className="border-b border-sand">
                  <td className="py-1.5 pr-2">
                    <Input
                      type="number" step="0.1"
                      value={r.up_to ?? ""}
                      onChange={(e) => setRule(idx, "up_to", e.target.value === "" ? null : parseFloat(e.target.value))}
                      placeholder="∞"
                      className="h-9"
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input type="number" step="0.01" value={r.price} onChange={(e) => setRule(idx, "price", parseFloat(e.target.value) || 0)} className="h-9" />
                  </td>
                  <td className="py-1.5 w-10">
                    <button type="button" onClick={() => removeRule(idx)} className="text-ash hover:text-red-600">
                      <X className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" onClick={addRule} className="text-xs uppercase tracking-[0.15em] text-copper hover:text-ink flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> Ajouter une tranche
          </button>

          <div className="pt-3 border-t border-stone flex items-center gap-3">
            <Lbl>Offerte au-dessus de (€, vide = jamais)</Lbl>
            <Input
              type="number" step="0.01"
              value={freeAbove ?? ""}
              onChange={(e) => update({ freeAbove: e.target.value === "" ? null : parseFloat(e.target.value) })}
              className="h-9 max-w-[140px]"
            />
          </div>
        </>
      )}
    </div>
  );
}

function MethodCard({ method, zones, onChange, onDelete }) {
  const update = (patch) => onChange({ ...method, ...patch });
  const updateZone = (zoneCode, patch) => {
    const z = { ...(method.zones || {}) };
    const current = z[zoneCode] || { enabled: false, rules: [], free_above: null };
    z[zoneCode] = {
      enabled: patch.enabled ?? current.enabled,
      rules: patch.rules ?? current.rules,
      free_above: patch.freeAbove !== undefined ? patch.freeAbove : current.free_above,
    };
    update({ zones: z });
  };

  return (
    <div data-testid={`method-${method.id}`} className={`bg-white border rounded-sm p-5 ${method.enabled ? "border-stone" : "border-stone/50 opacity-70"}`}>
      <div className="flex items-start gap-3 mb-4">
        <span className="p-2 bg-sand rounded-sm text-copper"><IconFor name={method.icon} /></span>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Input
              value={method.label} onChange={(e) => update({ label: e.target.value })}
              data-testid={`method-label-${method.id}`}
              className="font-serif text-lg border-0 border-b border-transparent focus:border-stone rounded-none px-0 h-auto py-1 bg-transparent shadow-none"
            />
            <label className="flex items-center gap-2 text-xs text-graphite shrink-0">
              <input type="checkbox" checked={method.enabled} onChange={(e) => update({ enabled: e.target.checked })} className="accent-copper" />
              Activée
            </label>
            <button type="button" onClick={onDelete} className="text-ash hover:text-red-600 shrink-0">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <Input
            placeholder="Description courte affichée au client"
            value={method.description || ""}
            onChange={(e) => update({ description: e.target.value })}
            className="mt-2 text-sm"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <div>
          <Lbl>Transporteur</Lbl>
          <select value={method.carrier} onChange={(e) => update({ carrier: e.target.value })} className="mt-1 w-full border border-stone rounded-sm p-2 text-sm bg-white">
            {CARRIERS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <Lbl>Délai estimé</Lbl>
          <Input value={method.delivery_time} onChange={(e) => update({ delivery_time: e.target.value })} placeholder="2-3 jours ouvrés" className="mt-1" />
        </div>
        <div>
          <Lbl>Base de calcul</Lbl>
          <select value={method.basis} onChange={(e) => update({ basis: e.target.value })} className="mt-1 w-full border border-stone rounded-sm p-2 text-sm bg-white">
            <option value="amount">Montant du panier</option>
            <option value="weight">Poids (kg)</option>
          </select>
        </div>
        <div>
          <Lbl>Icône</Lbl>
          <select value={method.icon} onChange={(e) => update({ icon: e.target.value })} className="mt-1 w-full border border-stone rounded-sm p-2 text-sm bg-white">
            {ICON_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <div className="space-y-3">
        {zones.map((z) => {
          const zcfg = method.zones?.[z.code] || { enabled: false, rules: [], free_above: null };
          return (
            <ZoneRulesEditor
              key={z.code}
              zone={z}
              rules={zcfg.rules || []}
              freeAbove={zcfg.free_above}
              enabled={!!zcfg.enabled}
              onChange={(patch) => updateZone(z.code, patch)}
            />
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Zones editor (compact)                                              */
/* ------------------------------------------------------------------ */
function ZonesEditor({ zones, onChange }) {
  const update = (idx, patch) => onChange(zones.map((z, i) => i === idx ? { ...z, ...patch } : z));
  const remove = (idx) => onChange(zones.filter((_, i) => i !== idx));
  const add = () => onChange([...zones, { code: `Z${zones.length + 1}`, label: "Nouvelle zone", countries: [] }]);

  return (
    <div className="bg-white border border-stone rounded-sm p-5 space-y-3">
      <h3 className="font-serif text-lg text-ink">Zones d&apos;expédition</h3>
      {zones.map((z, idx) => (
        <div key={idx} className="grid md:grid-cols-12 gap-2 items-end">
          <div className="md:col-span-2">
            <Lbl>Code</Lbl>
            <Input value={z.code} onChange={(e) => update(idx, { code: e.target.value.toUpperCase() })} className="mt-1" />
          </div>
          <div className="md:col-span-3">
            <Lbl>Libellé</Lbl>
            <Input value={z.label} onChange={(e) => update(idx, { label: e.target.value })} className="mt-1" />
          </div>
          <div className="md:col-span-6">
            <Lbl>Pays (séparés par virgule, ex: FR,France)</Lbl>
            <Input
              value={(z.countries || []).join(", ")}
              onChange={(e) => update(idx, { countries: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              className="mt-1"
            />
          </div>
          <div className="md:col-span-1">
            <button type="button" onClick={() => remove(idx)} className="text-ash hover:text-red-600 p-2">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
      <button type="button" onClick={add} className="text-xs uppercase tracking-[0.15em] text-copper hover:text-ink flex items-center gap-1">
        <Plus className="h-3.5 w-3.5" /> Ajouter une zone
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function AdminSettings() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/shipping").then(({ data }) => setConfig(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/shipping", { zones: config.zones, methods: config.methods });
      setConfig(data);
      toast.success("Configuration enregistrée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Réinitialiser toute la configuration aux valeurs par défaut ?")) return;
    try {
      const { data } = await api.post("/admin/shipping/reset");
      setConfig(data);
      toast.success("Configuration réinitialisée");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addMethod = () => {
    setConfig({ ...config, methods: [...config.methods, DEFAULT_METHOD()] });
  };
  const updateMethod = (idx, m) => {
    setConfig({ ...config, methods: config.methods.map((x, i) => i === idx ? m : x) });
  };
  const deleteMethod = (idx) => {
    if (!window.confirm("Supprimer cette méthode ?")) return;
    setConfig({ ...config, methods: config.methods.filter((_, i) => i !== idx) });
  };

  if (!config) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => <div key={i} className="h-32 bg-white border border-stone animate-pulse rounded-sm" />)}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-ash">Configuration</p>
          <h1 className="font-serif text-4xl text-ink tracking-tight">Frais de livraison</h1>
          <p className="text-graphite mt-2 max-w-2xl">
            Définissez vos zones, transporteurs et tarifs. Calcul dynamique selon le pays du client, le total du panier (ou le poids, si configuré).
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} className="text-xs uppercase tracking-[0.15em] text-ash hover:text-ink flex items-center gap-1.5 px-3 py-2 border border-stone">
            <RotateCcw className="h-3.5 w-3.5" /> Réinitialiser
          </button>
          <button onClick={save} disabled={saving} data-testid="save-shipping-btn" className="bg-ink text-cream px-5 py-2.5 text-xs uppercase tracking-[0.15em] flex items-center gap-2 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer la configuration"}
          </button>
        </div>
      </div>

      <div className="bg-sand/60 border border-stone rounded-sm p-4 my-6 text-sm text-graphite flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-copper shrink-0 mt-0.5" />
        <div>
          <p><strong className="text-ink">Comment ça marche :</strong> chaque méthode peut être activée par zone, avec des tranches de prix selon le montant du panier (ou le poids cumulé en kg si vous le configurez). Le seuil {`«Offerte au-dessus de»`} rend la livraison gratuite pour la zone. Si une méthode n&apos;a aucune règle correspondante au panier du client, elle n&apos;est pas proposée.</p>
        </div>
      </div>

      <ZonesEditor zones={config.zones} onChange={(zones) => setConfig({ ...config, zones })} />

      <div className="flex items-center justify-between mt-8 mb-4">
        <h2 className="font-serif text-2xl text-ink">Méthodes de livraison</h2>
        <button onClick={addMethod} data-testid="add-method-btn" className="text-xs uppercase tracking-[0.15em] text-copper hover:text-ink flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> Ajouter une méthode
        </button>
      </div>

      <div className="space-y-5">
        {config.methods.map((m, idx) => (
          <MethodCard
            key={`${m.id}-${idx}`}
            method={m}
            zones={config.zones}
            onChange={(nm) => updateMethod(idx, nm)}
            onDelete={() => deleteMethod(idx)}
          />
        ))}
      </div>

      <Simulator config={config} />

      <div className="mt-8 pb-12">
        <button onClick={save} disabled={saving} className="bg-ink text-cream px-6 py-3 text-xs uppercase tracking-[0.15em] flex items-center gap-2 disabled:opacity-50">
          <Save className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer la configuration"}
        </button>
      </div>
    </div>
  );
}
